import { FC } from 'react';
import { Box, Drawer, Stack, Typography, styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Logo } from 'src/components/logos/logo-kh';
import { Scrollbar } from 'src/components/scrollbar';
import { versionInfo } from 'src/config';
import { useTenantContext } from 'src/hooks/use-context';
import { paths } from 'src/paths';
import { NavigationSection } from './navigation-section';
import { SIDE_NAV_WIDTH, Section, useCssVars } from '../config';

const NavigationHeader = styled('div', {
  shouldForwardProp: (prop: string) => !['banner'].includes(prop)
})<{ banner: boolean }>(({ theme, banner }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  paddingTop: theme.spacing(banner ? 6 : 3)
}));

const NavigationScrollbar = styled(Scrollbar)({
  height: '100%',
  '& .simplebar-content': {
    height: '100%'
  },
  '& .simplebar-scrollbar:before': {
    background: 'var(--nav-scrollbar-color)'
  }
});

const NavigationFooter = styled('div')({
  padding: '1rem',
  position: 'relative',
  textAlign: 'center',
  display: 'block'
});

interface NavigationProps {
  sections: Section[];
  mobile?: boolean;
  banner?: boolean;
  open?: boolean;
  onClose?: () => void;
}

export const Navigation: FC<NavigationProps> = (props) => {
  const { sections, mobile, banner, open, onClose } = props;

  const cssVars = useCssVars('discreet');
  const { style } = useTenantContext();

  return (
    <Drawer
      anchor="left"
      open={!mobile || open}
      onClose={onClose}
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRightColor: 'var(--nav-border-color)',
          borderRightStyle: 'solid',
          borderRightWidth: 1,
          color: 'var(--nav-color)',
          width: SIDE_NAV_WIDTH
        }
      }}
      variant={mobile ? 'temporary' : 'permanent'}
    >
      <NavigationScrollbar>
        <Stack sx={{ height: '100%' }}>
          {/* Logo and application name */}
          <NavigationHeader banner={!!banner}>
            <Box
              component={Link}
              to={paths.index}
              sx={{ display: 'flex', height: 40, width: 40 }}
            >
              <Logo />
            </Box>
            <Typography variant="h6">{style.appName}</Typography>
          </NavigationHeader>

          {/* Navigation sections */}
          <Stack
            component="nav"
            spacing={2}
            sx={{ flexGrow: 1, px: 2 }}
          >
            {sections.map((section, index) => (
              <NavigationSection
                section={section}
                key={index}
              />
            ))}
          </Stack>

          {/* Version at the bottom of the navigation */}
          <NavigationFooter>
            <Typography
              color="var(--nav-item-disabled-color)"
              fontSize="7pt"
            >
              Front-End Version {versionInfo.version}
            </Typography>
            <Typography
              color="var(--nav-item-disabled-color)"
              fontSize="7pt"
            >
              Commit Hash {versionInfo.commitHash}
            </Typography>
          </NavigationFooter>
        </Stack>
      </NavigationScrollbar>
    </Drawer>
  );
};
